'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
const ACCEPTED_EXTS = ['mp4', 'webm', 'mov', 'avi', 'mkv'];

type OutputFormat = 'mp3' | 'wav' | 'm4a';

const FORMAT_INFO: Record<OutputFormat, { label: string; mime: string; desc: string }> = {
  mp3: { label: 'MP3', mime: 'audio/mpeg', desc: 'Widely compatible, smaller size' },
  wav: { label: 'WAV', mime: 'audio/wav', desc: 'Lossless, larger file size' },
  m4a: { label: 'M4A', mime: 'audio/mp4', desc: 'High quality, Apple compatible' },
};

export default function ExtractAudioFromVideoTool() {
  const [videoFile, setVideoFile] = useState<{ file: File; url: string } | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isValidType = ACCEPTED_VIDEO_TYPES.includes(file.type) || ACCEPTED_EXTS.includes(ext);
    if (!isValidType) {
      setError('Unsupported file type. Please upload a video file: MP4, WebM, or MOV.');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('File too large. Maximum supported size is 500 MB.');
      return;
    }
    setError(null);
    setResultUrl(null);
    const url = URL.createObjectURL(file);
    setVideoFile({ file, url });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const handleReset = () => {
    if (videoFile?.url) URL.revokeObjectURL(videoFile.url);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setVideoFile(null);
    setResultUrl(null);
    setResultSize(0);
    setError(null);
    setProgressPct(0);
    setProgressLabel('');
    setProcessing(false);
  };

  const handleExtract = async () => {
    if (!videoFile) return;
    setProcessing(true);
    setProgressPct(5);
    setProgressLabel('Loading ffmpeg…');
    setError(null);
    setResultUrl(null);

    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      setProgressPct(15);
      setProgressLabel('Initialising ffmpeg…');

      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgressPct(Math.round(25 + progress * 65));
        setProgressLabel(`Extracting audio… ${Math.round(progress * 100)}%`);
      });

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressPct(22);
      setProgressLabel('Reading video file…');

      const inputExt = videoFile.file.name.split('.').pop()?.toLowerCase() ?? 'mp4';
      const inputName = `input.${inputExt}`;
      const outputName = `output.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(videoFile.file));

      setProgressPct(25);
      setProgressLabel('Extracting audio track…');

      const ffmpegArgs: string[] = ['-i', inputName, '-vn'];

      if (outputFormat === 'mp3') {
        ffmpegArgs.push('-codec:a', 'libmp3lame', '-q:a', '2');
      } else if (outputFormat === 'wav') {
        ffmpegArgs.push('-codec:a', 'pcm_s16le');
      } else if (outputFormat === 'm4a') {
        ffmpegArgs.push('-codec:a', 'aac', '-b:a', '192k');
      }

      ffmpegArgs.push(outputName);

      await ffmpeg.exec(ffmpegArgs);

      setProgressPct(92);
      setProgressLabel('Finalising…');

      const data = await ffmpeg.readFile(outputName);
      const mimeType = FORMAT_INFO[outputFormat].mime;
      const blob = new Blob([data], { type: mimeType });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgressPct(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError(
        'Extraction failed. This may happen if the video has no audio track, or the format is not supported by your browser. Try a different file or format.'
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !videoFile) return;
    const baseName = videoFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_audio.${outputFormat}`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Zone */}
      {!videoFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
            isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🎬</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Video File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">MP4, WebM, MOV · Max 500 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,.webm,.mov,.avi,.mkv,video/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* File info bar */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🎬</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{videoFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(videoFile.file.size)}</p>
            </div>
            <button
              onClick={handleReset}
              title="Remove file"
              className="text-gray-400 hover:text-red-500 p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Video preview */}
          <div className="bg-gray-900 rounded-xl overflow-hidden">
            <video
              src={videoFile.url}
              controls
              className="w-full max-h-56 object-contain"
              preload="metadata"
            />
          </div>

          {/* Large file warning */}
          {videoFile.file.size > 100 * 1024 * 1024 && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-100 rounded-xl p-3">
              <svg className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-yellow-700 text-xs">
                Large videos may take longer because processing happens in your browser.
              </p>
            </div>
          )}

          {/* Output format */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Output Audio Format</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(FORMAT_INFO) as OutputFormat[]).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => { setOutputFormat(fmt); setResultUrl(null); }}
                  className={`px-3 py-3 rounded-xl text-sm font-semibold border transition-colors text-left ${
                    outputFormat === fmt
                      ? 'bg-amber-500 text-white border-amber-500' :'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-600'
                  }`}
                >
                  <span className="block font-bold">{FORMAT_INFO[fmt].label}</span>
                  <span className={`text-xs font-normal ${outputFormat === fmt ? 'text-amber-100' : 'text-gray-400'}`}>
                    {FORMAT_INFO[fmt].desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Progress bar */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">
                  Audio extracted! {FORMAT_INFO[outputFormat].label} · {formatBytes(resultSize)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview Extracted Audio</p>
                <audio src={resultUrl} controls className="w-full h-10" />
              </div>
              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {FORMAT_INFO[outputFormat].label}
              </button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            {!resultUrl && (
              <button
                onClick={handleExtract}
                disabled={processing}
                className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {processing ? 'Extracting…' : `Extract Audio as ${FORMAT_INFO[outputFormat].label}`}
              </button>
            )}
            {resultUrl && (
              <button
                onClick={() => { setResultUrl(null); setProgressPct(0); setProgressLabel(''); }}
                className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                Extract Again
              </button>
            )}
            <button
              onClick={handleReset}
              disabled={processing}
              className="px-4 py-3.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-600 font-semibold rounded-xl transition-colors text-sm"
              title="Reset"
            >
              ↺ Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
